import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Card, CardActionArea, CircularProgress, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import MainCard from 'ui-component/cards/MainCard';
import { getKpis } from 'services/kpi';
import { canAccessKpiMenu, KPI_ACCENT } from './kpiUtils';

const ACCENTS = ['#6E368C', '#1565C0', '#2E7D32', '#C62828', '#ED6C02', '#00838F'];

const pickIcon = (icone) => {
    const key = String(icone || '').toLowerCase();
    if (key.includes('line')) return ShowChartIcon;
    if (key.includes('pie') || key.includes('donut')) return PieChartIcon;
    return BarChartIcon;
};

const KpiCentral = () => {
    const user = useSelector((state) => state.auth.user);
    const allowed = canAccessKpiMenu(user);
    const navigate = useNavigate();
    const [kpis, setKpis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!allowed) return undefined;
        let active = true;
        getKpis()
            .then((response) => {
                if (!active) return;
                setKpis(response.data?.data || []);
            })
            .catch(() => {
                if (active) setError('Não foi possível carregar os KPIs.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [allowed]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return kpis;
        return kpis.filter(
            (kpi) =>
                String(kpi.titulo || '').toLowerCase().includes(term) ||
                String(kpi.descricao || '').toLowerCase().includes(term)
        );
    }, [kpis, search]);

    if (!allowed) {
        return <Navigate to="/index" replace />;
    }

    return (
        <MainCard contentSX={{ p: { xs: 2, md: 3 } }}>
            <Box
                sx={{
                    mb: 4,
                    p: { xs: 2.5, md: 4 },
                    borderRadius: 4,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #6E368C 0%, #4C1D6E 55%, #2A1638 100%)',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        right: -40,
                        top: -40,
                        width: 220,
                        height: 220,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.08)'
                    }}
                />
                <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 800, opacity: 0.85 }}>
                    Central de indicadores
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', mt: 0.5, mb: 1 }}>
                    KPIs
                </Typography>
                <Typography sx={{ maxWidth: 560, opacity: 0.9 }}>
                    Escolha um painel para acompanhar os números. Só aparecem os indicadores liberados para o seu usuário.
                </Typography>
                <TextField
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar KPI..."
                    size="small"
                    sx={{
                        mt: 3,
                        maxWidth: 420,
                        bgcolor: '#fff',
                        borderRadius: 2,
                        '& fieldset': { border: 'none' }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: KPI_ACCENT }} />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: KPI_ACCENT }} />
                </Box>
            )}

            {!loading && error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {!loading && !filtered.length && (
                <Box sx={{ py: 8, textAlign: 'center', color: '#8A7A98' }}>
                    {kpis.length ? 'Nenhum KPI encontrado nessa busca.' : 'Nenhum KPI liberado para o seu usuário.'}
                </Box>
            )}

            <Grid container spacing={2.5}>
                {filtered.map((kpi, index) => {
                    const Icon = pickIcon(kpi.icone);
                    const accent = ACCENTS[index % ACCENTS.length];
                    return (
                        <Grid item xs={12} sm={6} md={4} key={kpi.id || kpi.slug}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    borderRadius: 4,
                                    border: '1px solid #EDE4F5',
                                    overflow: 'hidden',
                                    transition: 'transform .2s ease, box-shadow .2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: `0 18px 40px ${accent}33`
                                    }
                                }}
                            >
                                <CardActionArea onClick={() => navigate(`/kpis/${kpi.slug}`)} sx={{ height: '100%', p: 0 }}>
                                    <Box sx={{ height: 8, bgcolor: accent }} />
                                    <Box sx={{ p: 2.5 }}>
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: 3,
                                                bgcolor: `${accent}14`,
                                                color: accent,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 2
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 28 }} />
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2A1638', mb: 0.75 }}>
                                            {kpi.titulo}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6B5A78', minHeight: 40 }}>
                                            {kpi.descricao || 'Painel de indicadores'}
                                        </Typography>
                                        <Box
                                            sx={{
                                                mt: 2.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                color: accent,
                                                fontWeight: 700
                                            }}
                                        >
                                            Abrir painel
                                            <ArrowForwardIcon fontSize="small" />
                                        </Box>
                                    </Box>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </MainCard>
    );
};

export default KpiCentral;
